import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { cleanNotifications, showNotification } from "@mantine/notifications";
import { ExportCampaignResultsParamsInput } from "src/graphql/generated/graphql";
import { exportCampaignResultsMutation } from "src/graphql/operations/exportCampaignResults/mutations";
import { graphqlFetch } from "src/graphql/utils/graphqlFetch";

import { handleError } from "../utils/error";
import { useAuthContext } from "./auth";
import { useSubscribeToExportJobUpdates } from "./system";

export const useExportCampaignResults = (campaignId: string) => {
    const { t } = useTranslation(["common"]);

    const { client: selectedClient } = useAuthContext();
    const [isExportingCampaignResults, setIsExportingCampaignResults] = useState(false);
    const [jobId, setJobId] = useState<string>();

    const { subscribeToExportJobUpdates } = useSubscribeToExportJobUpdates();

    const exportCampaignResults = useCallback(
        async (params: ExportCampaignResultsParamsInput) => {
            setIsExportingCampaignResults(true);
            setJobId(undefined);

            try {
                const response = await graphqlFetch(exportCampaignResultsMutation, {
                    campaignId,
                    clientId: selectedClient?.id,
                    params,
                });

                const jobId = response.exportCampaignResults;
                setJobId(jobId);

                const downloadUrl = await Promise.race([
                    new Promise<string>(async (resolve, reject) => {
                        const unsubscribe = await subscribeToExportJobUpdates(jobId, (jobUpdate) => {
                            if (jobUpdate?.job?.state === "finished") {
                                unsubscribe();
                                resolve(jobUpdate?.job?.downloadUrl);
                                return;
                            }

                            if (jobUpdate?.job?.state === "error") {
                                unsubscribe();
                                reject(jobUpdate?.job?.errorJson);
                                return;
                            }
                        });
                    }),
                    new Promise<string>((_, reject) => {
                        setTimeout(() => {
                            reject(new Error("Timeout"));
                        }, 1000 * 900);
                    }),
                ]);

                cleanNotifications();
                showNotification({
                    color: "green",
                    title: t("exporting_success"),
                    message: "",
                });

                return downloadUrl;
            } catch (err) {
                handleError(err, {
                    message: { key: "exporting_failed", namespace: "common" },
                    context: { campaignId, ...params, clientId: selectedClient?.id },
                });
            } finally {
                setIsExportingCampaignResults(false);
            }
        },
        [campaignId, subscribeToExportJobUpdates, t, selectedClient?.id]
    );

    return {
        isExportingCampaignResults,
        jobId,
        exportCampaignResults,
    };
};
